/**
 * @file taskService.ts
 * @brief Task API service. Uses backend /task_management/tasks; returns unwrapped payloads.
 */

import apiClient, { unwrapResponse } from './apiClient';
import { API_ENDPOINTS } from './endpoints';
import type {
  Task,
  TaskStatus,
  TaskFilters,
  PaginatedResponse,
  TaskComment,
  TaskRACI,
  Attachment,
  LinkedIssue,
} from '@/types';

const defaultRaci: TaskRACI = { responsible: [], accountable: '', consulted: [], informed: [] };

/** Normalize to date-only YYYY-MM-DD for MySQL DATE/DATETIME columns. */
function toDateOnly(value: string | undefined): string | undefined {
  if (value == null || value === '') return undefined;
  const s = typeof value === 'string' ? value : String(value);
  return s.slice(0, 10);
}

function mapRawToLinkedIssue(raw: Record<string, unknown>): LinkedIssue {
  return {
    id: String(raw.id ?? ''),
    title: String(raw.title ?? ''),
    description: raw.description != null ? String(raw.description) : undefined,
    provider: (raw.provider as LinkedIssue['provider']) ?? 'internal',
    externalId: raw.externalId != null ? String(raw.externalId) : undefined,
    externalUrl: raw.externalUrl != null ? String(raw.externalUrl) : undefined,
    status: (raw.status as LinkedIssue['status']) ?? 'open',
    priority: (raw.priority as LinkedIssue['priority']) ?? 'medium',
    type: (raw.type as LinkedIssue['type']) ?? 'task',
    subtaskId: raw.subtaskId != null ? String(raw.subtaskId) : undefined,
    labels: Array.isArray(raw.labels) ? (raw.labels as string[]) : [],
    assigneeIds: Array.isArray(raw.assigneeIds) ? (raw.assigneeIds as string[]) : [],
    linkedAt: String(raw.linkedAt ?? ''),
    linkedBy: String(raw.linkedBy ?? ''),
    resolvedAt: raw.resolvedAt != null ? String(raw.resolvedAt) : undefined,
    resolvedBy: raw.resolvedBy != null ? String(raw.resolvedBy) : undefined,
  };
}

function mapRawToDueDateChange(raw: Record<string, unknown>): DueDateChange {
  return {
    id: String(raw.id ?? ''),
    previousDueDate: String(raw.previousDueDate ?? ''),
    newDueDate: String(raw.newDueDate ?? ''),
    reason: String(raw.reason ?? ''),
    changedAt: String(raw.changedAt ?? ''),
    changedBy: String(raw.changedBy ?? ''),
    delayDays: Number(raw.delayDays ?? 0),
  };
}

function mapRawToTask(raw: Record<string, unknown>): Task {
  const raci = (raw.raci as TaskRACI) ?? defaultRaci;
  return {
    id: String(raw.id ?? ''),
    title: String(raw.title ?? ''),
    description: raw.description != null ? String(raw.description) : undefined,
    status: (raw.status as TaskStatus) ?? ('not_started' as TaskStatus),
    priority: (raw.priority as Task['priority']) ?? 'p3',
    projectId: raw.projectId != null ? String(raw.projectId) : undefined,
    milestoneId: raw.milestoneId != null ? String(raw.milestoneId) : undefined,
    parentTaskId: raw.parentTaskId != null ? String(raw.parentTaskId) : undefined,
    category: raw.category as Task['category'],
    raci: Array.isArray(raci.responsible) && (raci.accountable !== undefined || Array.isArray(raci.consulted))
      ? raci
      : defaultRaci,
    startDate: raw.startDate != null ? String(raw.startDate) : undefined,
    dueDate: String(raw.dueDate ?? ''),
    originalDueDate: raw.originalDueDate != null ? String(raw.originalDueDate) : undefined,
    totalDelayDays: raw.totalDelayDays != null ? Number(raw.totalDelayDays) : undefined,
    rescheduledFromDates: Array.isArray(raw.rescheduledFromDates) ? (raw.rescheduledFromDates as string[]) : [],
    wasRescheduledToday: Boolean(raw.wasRescheduledToday),
    dueDateHistory: Array.isArray(raw.dueDateHistory)
      ? (raw.dueDateHistory as Record<string, unknown>[]).map(mapRawToDueDateChange)
      : undefined,
    completedAt: raw.completedAt != null ? String(raw.completedAt) : undefined,
    estimatedHours: raw.estimatedHours != null ? Number(raw.estimatedHours) : undefined,
    actualHours: raw.actualHours != null ? Number(raw.actualHours) : undefined,
    order: Number(raw.order ?? 0),
    tags: Array.isArray(raw.tags) ? (raw.tags as string[]) : [],
    blockedByTaskIds: Array.isArray(raw.blockedByTaskIds) ? (raw.blockedByTaskIds as string[]) : [],
    blockingTaskIds: Array.isArray(raw.blockingTaskIds) ? (raw.blockingTaskIds as string[]) : [],
    subtaskIds: Array.isArray(raw.subtaskIds) ? (raw.subtaskIds as string[]) : [],
    attachments: Array.isArray(raw.attachments)
      ? (raw.attachments as Record<string, unknown>[]).map((a) => ({
          id: String(a.id),
          name: String(a.name ?? ''),
          url: String(a.url ?? ''),
          type: String(a.type ?? ''),
          size: Number(a.size ?? 0),
          uploadedAt: String(a.uploadedAt ?? ''),
          uploadedBy: String(a.uploadedBy ?? ''),
        }))
      : [],
    requiresReview: Boolean(raw.requiresReview),
    reviewStatus: raw.reviewStatus as Task['reviewStatus'],
    reviewerId: raw.reviewerId != null ? String(raw.reviewerId) : undefined,
    createdAt: String(raw.createdAt ?? ''),
    updatedAt: String(raw.updatedAt ?? ''),
    createdBy: raw.createdBy != null ? String(raw.createdBy) : undefined,
    linkedIssues: Array.isArray(raw.linkedIssues)
      ? (raw.linkedIssues as Record<string, unknown>[]).map(mapRawToLinkedIssue)
      : undefined,
    // Enriched name fields from backend
    raciNames: raw.raciNames as Task['raciNames'],
    projectName: raw.projectName != null ? String(raw.projectName) : undefined,
    createdByName: raw.createdByName != null ? String(raw.createdByName) : undefined,
    updatedByName: raw.updatedByName != null ? String(raw.updatedByName) : undefined,
    reviewerName: raw.reviewerName != null ? String(raw.reviewerName) : undefined,
  };
}

function mapListResponse(raw: {
  items?: unknown[];
  total?: number;
  page?: number;
  pageSize?: number;
  totalPages?: number;
  hasMore?: boolean;
}): PaginatedResponse<Task> {
  const items = Array.isArray(raw?.items)
    ? (raw.items as Record<string, unknown>[]).map(mapRawToTask)
    : [];
  return {
    items,
    total: Number(raw?.total ?? 0),
    page: Number(raw?.page ?? 1),
    pageSize: Number(raw?.pageSize ?? 20),
    totalPages: Number(raw?.totalPages ?? 1),
    hasMore: Boolean(raw?.hasMore),
  };
}

function buildListParams(filters?: TaskFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters?.status && filters.status.length) params.set('status', filters.status.join(','));
  if (filters?.projectId) params.set('projectId', filters.projectId);
  if (filters?.assigneeId) params.set('assigneeId', filters.assigneeId);
  return params;
}

export const taskService = {
  async getTasks(filters?: TaskFilters, page = 1, pageSize = 20): Promise<PaginatedResponse<Task>> {
    const params = buildListParams(filters);
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    const url = `${API_ENDPOINTS.TASKS.LIST}?${params.toString()}`;
    const response = await apiClient.get(url);
    const data = unwrapResponse<{ items: Record<string, unknown>[]; total: number; page: number; pageSize: number; totalPages: number; hasMore: boolean }>(response);
    return mapListResponse(data);
  },

  /**
   * Fetches current user's tasks with server-side pagination.
   * @param page 1-based page number.
   * @param pageSize Number of items per page.
   * @param filters Optional status, projectId, and raciRole for server-side filtering.
   * @returns Paginated list of tasks.
   */
  async getMyTasks(
    page = 1,
    pageSize = 20,
    filters?: Pick<TaskFilters, 'status' | 'projectId' | 'raciRole'>
  ): Promise<PaginatedResponse<Task>> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (filters?.status && filters.status.length) params.set('status', filters.status.join(','));
    if (filters?.projectId) params.set('projectId', filters.projectId);
    if (filters?.raciRole) params.set('raciRole', filters.raciRole);
    const response = await apiClient.get(`${API_ENDPOINTS.TASKS.MY_TASKS}?${params.toString()}`);
    const data = unwrapResponse<{
      items: Record<string, unknown>[];
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
      hasMore: boolean;
    }>(response);
    return mapListResponse(data);
  },

  async getTodaysTasks(): Promise<Task[]> {
    const response = await apiClient.get(API_ENDPOINTS.TASKS.TODAY);
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data) ? data.map(mapRawToTask) : [];
  },

  async getOverdueTasks(): Promise<Task[]> {
    const response = await apiClient.get(API_ENDPOINTS.TASKS.OVERDUE);
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data) ? data.map(mapRawToTask) : [];
  },

  async getUpcomingTasks(): Promise<Task[]> {
    const response = await apiClient.get(API_ENDPOINTS.TASKS.UPCOMING);
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data) ? data.map(mapRawToTask) : [];
  },

  async getTask(taskId: string): Promise<Task> {
    const response = await apiClient.get(API_ENDPOINTS.TASKS.GET(taskId));
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToTask(data);
  },

  async createTask(taskData: Partial<Task>): Promise<Task> {
    const body: Record<string, unknown> = {
      title: taskData.title ?? 'Untitled',
      description: taskData.description,
      status: taskData.status ?? 'not_started',
      priority: taskData.priority ?? 'p3',
      projectId: taskData.projectId,
      milestoneId: taskData.milestoneId,
      parentTaskId: taskData.parentTaskId,
      category: taskData.category,
      startDate: taskData.startDate != null ? toDateOnly(taskData.startDate) : undefined,
      dueDate: toDateOnly(taskData.dueDate) ?? new Date().toISOString().slice(0, 10),
      estimatedHours: taskData.estimatedHours,
      raci: taskData.raci ?? defaultRaci,
      tags: taskData.tags ?? [],
    };
    const response = await apiClient.post(API_ENDPOINTS.TASKS.CREATE, body);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToTask(data);
  },

  async updateTask(taskId: string, taskData: Partial<Task>): Promise<Task> {
    const body: Record<string, unknown> = {
      title: taskData.title,
      description: taskData.description,
      status: taskData.status,
      priority: taskData.priority,
      projectId: taskData.projectId,
      milestoneId: taskData.milestoneId,
      startDate: taskData.startDate != null ? toDateOnly(taskData.startDate) : undefined,
      dueDate: taskData.dueDate != null ? toDateOnly(taskData.dueDate) : undefined,
      estimatedHours: taskData.estimatedHours,
      actualHours: taskData.actualHours,
      category: taskData.category,
      originalDueDate: taskData.originalDueDate != null ? toDateOnly(taskData.originalDueDate) : undefined,
      totalDelayDays: taskData.totalDelayDays,
      rescheduledFromDates: taskData.rescheduledFromDates,
      dueDateHistory: taskData.dueDateHistory,
    };
    if (taskData.blockedByTaskIds !== undefined) body.blockedByTaskIds = taskData.blockedByTaskIds;
    if (taskData.blockingTaskIds !== undefined) body.blockingTaskIds = taskData.blockingTaskIds;
    const response = await apiClient.put(API_ENDPOINTS.TASKS.UPDATE(taskId), body);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToTask(data);
  },

  async deleteTask(taskId: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.TASKS.DELETE(taskId));
  },

  async getTasksByProject(projectId: string): Promise<Task[]> {
    const result = await this.getTasks({ projectId }, 1, 500);
    return result.items;
  },

  async updateTaskStatus(taskId: string, status: TaskStatus): Promise<Task> {
    const response = await apiClient.patch(API_ENDPOINTS.TASKS.UPDATE_STATUS(taskId), { status });
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToTask(data);
  },

  async completeTask(taskId: string): Promise<Task> {
    return this.updateTaskStatus(taskId, 'completed' as TaskStatus);
  },

  async reorderTasks(taskIds: string[], projectId?: string): Promise<void> {
    await apiClient.post(API_ENDPOINTS.TASKS.REORDER, { taskIds, projectId });
  },

  async getSubtasks(taskId: string): Promise<Task[]> {
    const response = await apiClient.get(API_ENDPOINTS.TASKS.SUBTASKS(taskId));
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data) ? data.map(mapRawToTask) : [];
  },

  async createSubtask(parentTaskId: string, subtaskData: Partial<Task>): Promise<Task> {
    const body = {
      ...subtaskData,
      parentTaskId,
    };
    const response = await apiClient.post(API_ENDPOINTS.TASKS.SUBTASKS(parentTaskId), body);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToTask(data);
  },

  async getComments(taskId: string): Promise<TaskComment[]> {
    const response = await apiClient.get(API_ENDPOINTS.TASKS.COMMENTS(taskId));
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data)
      ? data.map((c) => ({
          id: String(c.id),
          taskId,
          content: String(c.content ?? ''),
          parentCommentId: c.parentCommentId != null ? String(c.parentCommentId) : undefined,
          mentions: Array.isArray(c.mentions) ? (c.mentions as string[]) : [],
          createdAt: String(c.createdAt ?? ''),
          updatedAt: c.updatedAt != null ? String(c.updatedAt) : undefined,
          createdBy: c.createdBy != null ? String(c.createdBy) : undefined,
          createdByName: c.createdByName != null ? String(c.createdByName) : undefined,
        }))
      : [];
  },

  async addComment(taskId: string, content: string, mentions?: string[], parentCommentId?: string): Promise<TaskComment> {
    const body: Record<string, unknown> = { content };
    if (parentCommentId) body.parentCommentId = parentCommentId;
    const response = await apiClient.post(API_ENDPOINTS.TASKS.ADD_COMMENT(taskId), body);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return {
      id: String(data.id),
      taskId,
      content: String(data.content ?? ''),
      parentCommentId: data.parentCommentId != null ? String(data.parentCommentId) : undefined,
      mentions: mentions ?? [],
      createdAt: String(data.createdAt ?? ''),
      createdBy: String(data.createdBy ?? ''),
    };
  },

  async getAttachments(taskId: string): Promise<Attachment[]> {
    const response = await apiClient.get(API_ENDPOINTS.TASKS.ATTACHMENTS(taskId));
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data)
      ? data.map((a) => ({
          id: String(a.id),
          name: String(a.name ?? ''),
          url: String(a.url ?? ''),
          type: String(a.type ?? ''),
          size: Number(a.size ?? 0),
          uploadedAt: String(a.uploadedAt ?? ''),
          uploadedBy: String(a.uploadedBy ?? ''),
        }))
      : [];
  },

  async uploadAttachment(
    taskId: string,
    payload: { name: string; url: string; type?: string; size?: number }
  ): Promise<{ id: string; url: string }> {
    const body = {
      name: payload.name,
      url: payload.url,
      type: payload.type ?? 'application/octet-stream',
      size: payload.size ?? 0,
    };
    const response = await apiClient.post(API_ENDPOINTS.TASKS.UPLOAD_ATTACHMENT(taskId), body);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return { id: String(data.id), url: String(data.url ?? '') };
  },

  async deleteAttachment(taskId: string, attachmentId: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.TASKS.DELETE_ATTACHMENT(taskId, attachmentId));
  },

  async getLinkedIssues(taskId: string): Promise<LinkedIssue[]> {
    const response = await apiClient.get(API_ENDPOINTS.TASKS.ISSUES(taskId));
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data) ? data.map(mapRawToLinkedIssue) : [];
  },

  async addLinkedIssue(taskId: string, payload: Omit<LinkedIssue, 'id' | 'linkedAt' | 'linkedBy'>): Promise<LinkedIssue> {
    const body = {
      title: payload.title,
      description: payload.description,
      provider: payload.provider ?? 'internal',
      externalId: payload.externalId,
      externalUrl: payload.externalUrl,
      status: payload.status ?? 'open',
      priority: payload.priority ?? 'medium',
      type: payload.type ?? 'task',
      subtaskId: payload.subtaskId,
      labels: payload.labels ?? [],
      assigneeIds: payload.assigneeIds ?? [],
    };
    const response = await apiClient.post(API_ENDPOINTS.TASKS.ISSUES(taskId), body);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToLinkedIssue(data);
  },

  async updateLinkedIssue(taskId: string, issueId: string, payload: Partial<LinkedIssue>): Promise<LinkedIssue> {
    const body: Record<string, unknown> = {};
    if (payload.title !== undefined) body.title = payload.title;
    if (payload.description !== undefined) body.description = payload.description;
    if (payload.status !== undefined) body.status = payload.status;
    if (payload.priority !== undefined) body.priority = payload.priority;
    if (payload.type !== undefined) body.type = payload.type;
    if (payload.subtaskId !== undefined) body.subtaskId = payload.subtaskId;
    if (payload.labels !== undefined) body.labels = payload.labels;
    if (payload.assigneeIds !== undefined) body.assigneeIds = payload.assigneeIds;
    if (payload.resolvedAt !== undefined) body.resolvedAt = payload.resolvedAt;
    if (payload.resolvedBy !== undefined) body.resolvedBy = payload.resolvedBy;
    const response = await apiClient.put(API_ENDPOINTS.TASKS.ISSUE(taskId, issueId), body);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToLinkedIssue(data);
  },

  async deleteLinkedIssue(taskId: string, issueId: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.TASKS.ISSUE(taskId, issueId));
  },

  async assignTask(taskId: string, userId: string, role = 'responsible'): Promise<void> {
    await apiClient.post(API_ENDPOINTS.TASKS.ASSIGN(taskId), { userId, role });
  },

  async unassignTask(taskId: string, userId: string, role = 'responsible'): Promise<void> {
    await apiClient.post(API_ENDPOINTS.TASKS.UNASSIGN(taskId), { userId, role });
  },
};

export default taskService;
